import * as classnames from 'classnames';
import * as React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { StarsSelector } from './StarsSelector';
import { ReviewsNumber } from './ReviewsNumber';
import { ReadReviews } from './ReadReviews';
import { observable, action } from 'mobx';
import { starsWrapperTheme, ratingTheme } from '../theme/default-theme';
import { ContentSpacing } from 'elc-base-theme';
import { AverageRating } from './AverageRating';
import { StarsSimplified } from './StarsSimplified';

export interface IRating {
    className?: string;
    percentageRating: number;
    reviewsNumber: number;
    selectedRating: number;
    showReviews?: boolean;
    showReadReviews?: boolean;
    isDisabled: boolean;
    readReviewsLabel: string;
    firstToWriteReviewLabel: string;
    numberOfStars: number;
    onRatingClick(numbersOfStars: number): void;
    onReadReviewsClick?(): void;
    onWriteReviewsClick?(): void;
    productLink?: string;
    hideStars?: boolean;
    hideReviewsLabel?: boolean;
    reviewsNumberLabel?: string;
    averageRating: number;
    showSimplifiedStars?: boolean;
}

const RatingWrapper = styled.div`
    margin: ${ContentSpacing.space12} 0;
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    ${ratingTheme};
`;

const StarsWrapper = styled.div`
    margin: 0;
    display: inline-block;
    position: relative;
    padding: 0;
    user-select: none;
    ${starsWrapperTheme};
`;

@observer
export class Rating extends React.Component<IRating> {
    @observable public showOverlay: boolean = false;
    constructor(props: IRating) {
        super(props);
    }

    @action
    public toggleOverlay = () => {
        if (!this.props.isDisabled) {
            this.showOverlay = !this.showOverlay;
        }
    };

    public render() {
        const {
            numberOfStars,
            isDisabled,
            percentageRating,
            reviewsNumber,
            selectedRating,
            onReadReviewsClick,
            onWriteReviewsClick,
            readReviewsLabel,
            firstToWriteReviewLabel,
            showReviews,
            hideStars,
            showReadReviews,
            onRatingClick,
            hideReviewsLabel,
            reviewsNumberLabel,
            productLink,
            averageRating,
            className
        } = this.props;

        const ratingClassNames = classnames('elc-rating', 'js-rating', className);
        const starsWrapperClassnames = classnames('elc-stars-wrapper', {
            'elc-stars-disabled': isDisabled
        });

        const starsRatingArray = [...Array(numberOfStars)].fill(0);
        const shouldShowStars = !!reviewsNumber && !hideStars;
        const shouldShowAvgRating = !!reviewsNumber && !!averageRating;

        return (
            <RatingWrapper className={ratingClassNames}>
                {shouldShowStars && (
                    <StarsWrapper
                        className={starsWrapperClassnames}
                        onMouseEnter={this.toggleOverlay}
                        onMouseLeave={this.toggleOverlay}
                    >
                        {isDisabled ? (
                            <StarsSimplified percentageRating={percentageRating} />
                        ) : (
                            <StarsSelector
                                starsArray={starsRatingArray}
                                selectedRating={selectedRating}
                                clickHandler={onRatingClick}
                            />
                        )}
                    </StarsWrapper>
                )}
                {shouldShowAvgRating && (
                    <AverageRating numberOfStars={numberOfStars} averageRating={averageRating} />
                )}
                {shouldShowStars && showReviews && (
                    <ReviewsNumber
                        reviewsNumber={reviewsNumber}
                        reviewsNumberLabel={reviewsNumberLabel}
                    />
                )}
                {showReadReviews && (
                    <ReadReviews
                        onReadReviewsClick={onReadReviewsClick}
                        onWriteReviewsClick={onWriteReviewsClick}
                        readReviewsLabel={readReviewsLabel}
                        reviewsNumber={reviewsNumber}
                        firstToWriteReviewLabel={firstToWriteReviewLabel}
                        hideReviewsLabel={hideReviewsLabel}
                        productLink={productLink}
                    />
                )}
            </RatingWrapper>
        );
    }
}
